import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useCartStore } from "../../store/useStore";
import PaymentForm from "../Payment/PaymentForm";
import { getPaymentErrorMessage } from "../../utils/paymentErrorMessages";

const provinces = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" },
];

const taxRules: Record<string, { label: string; rate: number }[]> = {
  AB: [{ label: "GST", rate: 0.05 }],
  BC: [
    { label: "GST", rate: 0.05 },
    { label: "PST", rate: 0.07 },
  ],
  MB: [
    { label: "GST", rate: 0.05 },
    { label: "RST", rate: 0.07 },
  ],
  NB: [{ label: "HST", rate: 0.15 }],
  NL: [{ label: "HST", rate: 0.15 }],
  NT: [{ label: "GST", rate: 0.05 }],
  NS: [{ label: "HST", rate: 0.15 }],
  NU: [{ label: "GST", rate: 0.05 }],
  ON: [{ label: "HST", rate: 0.13 }],
  PE: [{ label: "HST", rate: 0.15 }],
  QC: [
    { label: "GST", rate: 0.05 },
    { label: "QST", rate: 0.09975 },
  ],
  SK: [
    { label: "GST", rate: 0.05 },
    { label: "PST", rate: 0.06 },
  ],
  YT: [{ label: "GST", rate: 0.05 }],
};

// Shipping rates based on province/region
const getShippingCost = (province: string): number => {
  // Local / nearby: Ontario + Quebec
  if (province === "ON" || province === "QC") {
    return 8;
  }
  // Central/West: Manitoba, Saskatchewan, Alberta
  if (province === "MB" || province === "SK" || province === "AB") {
    return 10;
  }
  // West Coast: British Columbia
  if (province === "BC") {
    return 12;
  }
  // Atlantic: New Brunswick, Nova Scotia, Prince Edward Island, Newfoundland & Labrador
  if (
    province === "NB" ||
    province === "NS" ||
    province === "PE" ||
    province === "NL"
  ) {
    return 11;
  }
  // Remote / Northern: Yukon, Northwest Territories, Nunavut
  if (province === "YT" || province === "NT" || province === "NU") {
    return 15;
  }
  // Default fallback
  return 10;
};

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: #f8f8f8;
  padding: 2rem 0;
`;

const CheckoutContainer = styled.div`
  display: flex;
  background: #fff;
  border-radius: 1.2rem;
  box-shadow: 0 2px 16px rgba(233, 125, 125, 0.09);
  width: 100%;
  max-width: 1100px;
  min-height: 600px;
  overflow: hidden;
  @media (max-width: 900px) {
    flex-direction: column;
    max-width: 98vw;
  }
`;

const LeftPanel = styled.div`
  flex: 1.2;
  padding: 2.5rem 2.5rem 2.5rem 2.5rem;
  background: #fff;
  @media (max-width: 900px) {
    padding: 1.5rem 1rem;
  }
`;

const RightPanel = styled.div`
  flex: 1;
  background: #faf9f9;
  padding: 2.5rem 2rem;
  border-left: 1.5px solid #f0eaea;
  @media (max-width: 900px) {
    border-left: none;
    border-top: 1.5px solid #f0eaea;
    padding: 1.5rem 1rem;
  }
`;

const Section = styled.section`
  margin-bottom: 2.2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
  color: #e97d7dff;
  margin-bottom: 1.1rem;
`;

const InputRow = styled.div`
  display: flex;
  gap: 1.2rem;
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0.7rem;
  }
`;

const Label = styled.label`
  font-size: 1rem;
  color: #444;
  margin-bottom: 0.3rem;
  display: block;
`;

const Input = styled.input<{ error?: boolean }>`
  width: 100%;
  padding: 0.7rem 1rem;
  border-radius: 0.7rem;
  border: 1.5px solid ${(p) => (p.error ? "#e97d7dff" : "#e0e0e0")};
  font-size: 1rem;
  margin-bottom: 0.7rem;
  background: #fafafa;
  transition: border 0.2s;
  &:focus {
    border-color: #e97d7dff;
    outline: none;
  }
`;

const Select = styled.select<{ error?: boolean }>`
  width: 100%;
  padding: 0.7rem 1rem;
  border-radius: 0.7rem;
  border: 1.5px solid ${(p) => (p.error ? "#e97d7dff" : "#e0e0e0")};
  font-size: 1rem;
  margin-bottom: 0.7rem;
  background: #fafafa;
  transition: border 0.2s;
  &:focus {
    border-color: #e97d7dff;
    outline: none;
  }
`;

const ErrorMsg = styled.div`
  color: #e97d7dff;
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
`;

const PaymentErrorContainer = styled.div`
  color: #d32f2f;
  background: #ffebee;
  padding: 1.2rem 1.5rem;
  border-radius: 0.8rem;
  margin-bottom: 1.5rem;
  border: 2px solid #ffcdd2;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    padding: 1rem;
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    padding: 0.9rem;
    font-size: 0.9rem;
  }
`;

const ErrorIcon = styled.div`
  font-size: 1.5rem;
  flex-shrink: 0;
  margin-top: 0.1rem;

  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const ErrorContent = styled.div`
  flex: 1;
`;

const ErrorTitle = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #c62828;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
  }
`;

const ErrorMessage = styled.div`
  font-size: 1rem;
  line-height: 1.5;
  color: #d32f2f;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const ErrorAction = styled.div`
  margin-top: 0.8rem;
  font-size: 0.9rem;
  color: #666;
  font-style: italic;

  @media (max-width: 480px) {
    font-size: 0.85rem;
    margin-top: 0.6rem;
  }
`;

const ErrorButtonContainer = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.8rem;
  }
`;

const ErrorButton = styled.button`
  background: #e97d7d;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #d66b6b;
  }

  &.secondary {
    background: transparent;
    color: #e97d7d;
    border: 2px solid #e97d7d;

    &:hover {
      background: #ffeaea;
    }
  }

  @media (max-width: 480px) {
    width: 100%;
    padding: 0.8rem;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1.2rem;
  margin-top: 2.2rem;
`;

const Button = styled.button<{ secondary?: boolean }>`
  background: ${(p) => (p.secondary ? "#fff" : "#e97d7dff")};
  color: ${(p) => (p.secondary ? "#e97d7dff" : "#fff")};
  border: ${(p) => (p.secondary ? "2px solid #e97d7dff" : "none")};
  border-radius: 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0.9rem 2.2rem;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: ${(p) => (p.secondary ? "#e97d7dff" : "#fff")};
    color: ${(p) => (p.secondary ? "#fff" : "#e97d7dff")};
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ProductList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem 0;
`;

const ProductItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 1px solid #f0eaea;
`;

const ProductImage = styled.img`
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 0.7rem;
  margin-right: 1.1rem;
  background: #f7f7f7;
`;

const ProductName = styled.span`
  flex: 2;
  color: #222;
  font-size: 1.08rem;
`;

const ProductQty = styled.span`
  min-width: 28px;
  text-align: center;
  color: #888;
  font-size: 1.08rem;
`;

const ProductPrice = styled.span`
  flex: 0 0 70px;
  text-align: right;
  color: #e97d7dff;
  font-weight: 600;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 1.08rem;
  margin-bottom: 0.7rem;
`;

const SummaryTotal = styled(SummaryRow)`
  font-size: 1.18rem;
  font-weight: 700;
  color: #e97d7dff;
`;

const SuccessMsg = styled.div`
  background: #e7fbe7;
  color: #1ca97a;
  border-radius: 0.7rem;
  padding: 1.2rem 1rem;
  text-align: center;
  font-size: 1.15rem;
  margin-top: 2rem;
`;

// Validation helpers
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const postalRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;

function calcTaxes(subtotal: number, province: string) {
  const rules = taxRules[province] || [];
  return rules.map((rule) => ({
    label: rule.label,
    amount: +(subtotal * rule.rate).toFixed(2),
  }));
}

const CheckoutPage: React.FC = () => {
  const items = useCartStore((state) => state.items);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [form, setForm] = useState({
    email: "",
    phone: "",
    name: "",
    address1: "",
    address2: "",
    city: "",
    province: "ON",
    postal: "",
    country: "Canada",
    useShippingAsBilling: true,
    billingName: "",
    billingAddress1: "",
    billingAddress2: "",
    billingCity: "",
    billingProvince: "ON",
    billingPostal: "",
    billingCountry: "Canada",
  });
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [loading] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);

  useEffect(() => {
    if (user) setShowGuestForm(true);
  }, [user]);

  const subtotal = items.reduce((sum, i) => sum + i.price * i.qty, 0);
  // Calculate shipping based on shipping address province
  const shippingCost = getShippingCost(form.province);
  // Calculate taxes based on billing address province (or shipping if same)
  const billingProvince = form.useShippingAsBilling
    ? form.province
    : form.billingProvince;
  const taxes = calcTaxes(subtotal + shippingCost, billingProvince);
  const total =
    subtotal + shippingCost + taxes.reduce((sum, t) => sum + t.amount, 0);

  // Validation
  const validate = () => {
    const newErrors: { [k: string]: string } = {};
    if (!emailRegex.test(form.email)) newErrors.email = "Invalid email format.";
    if (!form.phone) newErrors.phone = "Phone number required.";
    if (!form.name) newErrors.name = "Full name required.";
    if (!form.address1) newErrors.address1 = "Address required.";
    if (!form.city) newErrors.city = "City required.";
    if (!form.postal || !postalRegex.test(form.postal))
      newErrors.postal = "Invalid Canadian postal code.";

    // Billing address validation (only if not using shipping address)
    if (!form.useShippingAsBilling) {
      if (!form.billingName) newErrors.billingName = "Billing name required.";
      if (!form.billingAddress1)
        newErrors.billingAddress1 = "Billing address required.";
      if (!form.billingCity) newErrors.billingCity = "Billing city required.";
      if (!form.billingPostal || !postalRegex.test(form.billingPostal))
        newErrors.billingPostal = "Invalid Canadian postal code.";
    }

    return newErrors;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const target = e.target;
    const value =
      target.type === "checkbox"
        ? (target as HTMLInputElement).checked
        : target.value;
    const name = target.name;

    if (name === "useShippingAsBilling") {
      setForm({ ...form, [name]: value as boolean });
    } else {
      setForm({ ...form, [name]: value });
    }

    // Remove the error key instead of setting to undefined to satisfy TS
    const { [name]: removed, ...rest } = errors;
    setErrors(rest);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    // Show both order summary and payment form together
    setShowOrderSummary(true);
    setShowPaymentForm(true);
  };

  const handleEditOrder = () => {
    setOrderError(null);
    setShowOrderSummary(false);
    setShowPaymentForm(false);
  };

  const handlePaymentSuccess = (orderId: string) => {
    // Store order details for success page
    const orderData = {
      orderId,
      items: items.map((item) => ({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.qty,
        image: item.images?.[0],
      })),
      customerInfo: {
        name: form.name,
        email: form.email,
        phone: form.phone,
      },
      shippingAddress: {
        name: form.name,
        address1: form.address1,
        address2: form.address2,
        city: form.city,
        province: form.province,
        postal: form.postal,
        country: form.country,
      },
      billingAddress: form.useShippingAsBilling
        ? {
            name: form.name,
            address1: form.address1,
            address2: form.address2,
            city: form.city,
            province: form.province,
            postal: form.postal,
            country: form.country,
          }
        : {
            name: form.billingName,
            address1: form.billingAddress1,
            address2: form.billingAddress2,
            city: form.billingCity,
            province: form.billingProvince,
            postal: form.billingPostal,
            country: form.billingCountry,
          },
      shippingMethod: `Standard Shipping (${
        provinces.find((p) => p.code === form.province)?.name || form.province
      })`,
      subtotal: subtotal,
      shippingCost: shippingCost,
      taxes: taxes.reduce((sum, t) => sum + t.amount, 0),
      total: total,
      status: "confirmed",
      orderDate: new Date().toISOString(),
    };

    // Clear cart on successful payment
    items.forEach((item) => {
      useCartStore.getState().removeFromCart(item.id);
    });

    // Navigate to order confirmation page with order data
    navigate("/order-confirmation", { state: { orderDetails: orderData } });
  };

  const handlePaymentError = (error: string) => {
    // Error message is already user-friendly from PaymentForm
    // But we can ensure it's properly formatted
    const userFriendlyError = getPaymentErrorMessage(error);
    setOrderError(userFriendlyError);
    setShowPaymentForm(false);
    // Keep order summary visible so user can see their order
    setShowOrderSummary(true);
    // Scroll to error message
    setTimeout(() => {
      const errorElement = document.querySelector("[data-payment-error]");
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };

  const handleRetryPayment = () => {
    setOrderError(null);
    setShowPaymentForm(true);
  };

  // Order Summary Component
  const OrderSummary = () => (
    <div style={{ marginBottom: "2rem" }}>
      <SectionTitle style={{ fontSize: "1.5rem", marginBottom: "1.5rem" }}>
        Review Your Order
      </SectionTitle>

      {/* Personal Information */}
      <div style={{ marginBottom: "1.5rem" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "0.8rem",
          }}
        >
          <h3 style={{ fontSize: "1.1rem", margin: 0, color: "#333" }}>
            Personal Information
          </h3>
          <button
            type="button"
            onClick={handleEditOrder}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "0.5rem",
              borderRadius: "0.25rem",
              color: "#e97d7dff",
              fontSize: "1.2rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Edit Personal Information"
          >
            ✏️
          </button>
        </div>
        <div
          style={{
            background: "#f9f9f9",
            padding: "1rem",
            borderRadius: "0.5rem",
          }}
        >
          <p>
            <strong>Name:</strong> {form.name}
          </p>
          <p>
            <strong>Email:</strong> {form.email}
          </p>
          <p>
            <strong>Phone:</strong> {form.phone}
          </p>
        </div>
      </div>

      {/* Shipping Address */}
      <div style={{ marginBottom: "1.5rem" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "0.8rem",
          }}
        >
          <h3 style={{ fontSize: "1.1rem", margin: 0, color: "#333" }}>
            Shipping Address
          </h3>
          <button
            type="button"
            onClick={handleEditOrder}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "0.5rem",
              borderRadius: "0.25rem",
              color: "#e97d7dff",
              fontSize: "1.2rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Edit Shipping Address"
          >
            ✏️
          </button>
        </div>
        <div
          style={{
            background: "#f9f9f9",
            padding: "1rem",
            borderRadius: "0.5rem",
          }}
        >
          <p>{form.name}</p>
          <p>{form.address1}</p>
          {form.address2 && <p>{form.address2}</p>}
          <p>
            {form.city}, {form.province} {form.postal}
          </p>
          <p>{form.country}</p>
        </div>
      </div>

      {/* Billing Address */}
      <div style={{ marginBottom: "1.5rem" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "0.8rem",
          }}
        >
          <h3 style={{ fontSize: "1.1rem", margin: 0, color: "#333" }}>
            Billing Address
          </h3>
          <button
            type="button"
            onClick={handleEditOrder}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "0.5rem",
              borderRadius: "0.25rem",
              color: "#e97d7dff",
              fontSize: "1.2rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Edit Billing Address"
          >
            ✏️
          </button>
        </div>
        <div
          style={{
            background: "#f9f9f9",
            padding: "1rem",
            borderRadius: "0.5rem",
          }}
        >
          {form.useShippingAsBilling ? (
            <>
              <p style={{ fontStyle: "italic", color: "#666" }}>
                Same as shipping address
              </p>
            </>
          ) : (
            <>
              <p>{form.billingName}</p>
              <p>{form.billingAddress1}</p>
              {form.billingAddress2 && <p>{form.billingAddress2}</p>}
              <p>
                {form.billingCity}, {form.billingProvince} {form.billingPostal}
              </p>
              <p>{form.billingCountry}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <PageWrapper>
      <CheckoutContainer>
        <LeftPanel>
          {!showGuestForm && !user && (
            <Section
              style={{
                marginBottom: "2.5rem",
                border: "1.5px solid #f0eaea",
                borderRadius: "1rem",
                padding: "2rem 1.5rem",
                background: "#faf9f9",
                boxShadow: "0 2px 8px rgba(233,125,125,0.04)",
              }}
            >
              <SectionTitle
                style={{
                  fontSize: "1.3rem",
                  marginBottom: "1.5rem",
                  textAlign: "center",
                }}
              >
                Welcome to Checkout
              </SectionTitle>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1.2rem",
                  alignItems: "center",
                }}
              >
                <Button
                  style={{ width: "100%", maxWidth: 320 }}
                  onClick={() => navigate("/signin")}
                >
                  Sign In
                </Button>
                <Button
                  style={{ width: "100%", maxWidth: 320 }}
                  secondary
                  onClick={() => navigate("/signup")}
                >
                  Sign Up
                </Button>
                <div
                  style={{
                    color: "#888",
                    fontSize: "1.05rem",
                    margin: "0.7rem 0",
                  }}
                >
                  or
                </div>
                <Button
                  style={{ width: "100%", maxWidth: 320 }}
                  onClick={() => setShowGuestForm(true)}
                >
                  Continue as Guest
                </Button>
              </div>
            </Section>
          )}
          {showGuestForm && !showOrderSummary && (
            <form onSubmit={handleSubmit} autoComplete="off">
              <Section>
                <SectionTitle>Contact Information</SectionTitle>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  autoComplete="email"
                  required
                  aria-invalid={!!errors.email}
                />
                {errors.email && <ErrorMsg>{errors.email}</ErrorMsg>}
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  error={!!errors.phone}
                  autoComplete="tel"
                  required
                  aria-invalid={!!errors.phone}
                />
                {errors.phone && <ErrorMsg>{errors.phone}</ErrorMsg>}
              </Section>
              <Section>
                <SectionTitle>Shipping Address</SectionTitle>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  required
                  aria-invalid={!!errors.name}
                />
                {errors.name && <ErrorMsg>{errors.name}</ErrorMsg>}
                <Label htmlFor="address1">Address 1</Label>
                <Input
                  id="address1"
                  name="address1"
                  value={form.address1}
                  onChange={handleChange}
                  error={!!errors.address1}
                  required
                  aria-invalid={!!errors.address1}
                />
                {errors.address1 && <ErrorMsg>{errors.address1}</ErrorMsg>}
                <Label htmlFor="address2">
                  Address 2{" "}
                  <span style={{ color: "#aaa", fontWeight: 400 }}>
                    (optional)
                  </span>
                </Label>
                <Input
                  id="address2"
                  name="address2"
                  value={form.address2}
                  onChange={handleChange}
                  error={!!errors.address2}
                />
                <InputRow>
                  <div style={{ flex: 1 }}>
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      name="city"
                      value={form.city}
                      onChange={handleChange}
                      error={!!errors.city}
                      required
                      aria-invalid={!!errors.city}
                    />
                    {errors.city && <ErrorMsg>{errors.city}</ErrorMsg>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <Label htmlFor="province">Province</Label>
                    <Select
                      id="province"
                      name="province"
                      value={form.province}
                      onChange={handleChange}
                      error={!!errors.province}
                      required
                      aria-invalid={!!errors.province}
                    >
                      {provinces.map((prov) => (
                        <option key={prov.code} value={prov.code}>
                          {prov.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                </InputRow>
                <Label htmlFor="postal">Postal Code</Label>
                <Input
                  id="postal"
                  name="postal"
                  value={form.postal}
                  onChange={handleChange}
                  error={!!errors.postal}
                  required
                  aria-invalid={!!errors.postal}
                  placeholder="A1A 1A1"
                />
                {errors.postal && <ErrorMsg>{errors.postal}</ErrorMsg>}
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  name="country"
                  value="Canada"
                  disabled
                  style={{ background: "#f0f0f0" }}
                />
              </Section>
              <Section>
                <SectionTitle>Billing Address</SectionTitle>
                <div style={{ marginBottom: "1rem" }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      name="useShippingAsBilling"
                      checked={form.useShippingAsBilling}
                      onChange={handleChange}
                      style={{ marginRight: "0.5rem", cursor: "pointer" }}
                    />
                    <span>Use shipping address as billing address</span>
                  </label>
                </div>
                {!form.useShippingAsBilling && (
                  <>
                    <Label htmlFor="billingName">Full Name</Label>
                    <Input
                      id="billingName"
                      name="billingName"
                      value={form.billingName}
                      onChange={handleChange}
                      error={!!errors.billingName}
                      required
                      aria-invalid={!!errors.billingName}
                    />
                    {errors.billingName && (
                      <ErrorMsg>{errors.billingName}</ErrorMsg>
                    )}
                    <Label htmlFor="billingAddress1">Address 1</Label>
                    <Input
                      id="billingAddress1"
                      name="billingAddress1"
                      value={form.billingAddress1}
                      onChange={handleChange}
                      error={!!errors.billingAddress1}
                      required
                      aria-invalid={!!errors.billingAddress1}
                    />
                    {errors.billingAddress1 && (
                      <ErrorMsg>{errors.billingAddress1}</ErrorMsg>
                    )}
                    <Label htmlFor="billingAddress2">
                      Address 2{" "}
                      <span style={{ color: "#aaa", fontWeight: 400 }}>
                        (optional)
                      </span>
                    </Label>
                    <Input
                      id="billingAddress2"
                      name="billingAddress2"
                      value={form.billingAddress2}
                      onChange={handleChange}
                      error={!!errors.billingAddress2}
                    />
                    <InputRow>
                      <div style={{ flex: 1 }}>
                        <Label htmlFor="billingCity">City</Label>
                        <Input
                          id="billingCity"
                          name="billingCity"
                          value={form.billingCity}
                          onChange={handleChange}
                          error={!!errors.billingCity}
                          required
                          aria-invalid={!!errors.billingCity}
                        />
                        {errors.billingCity && (
                          <ErrorMsg>{errors.billingCity}</ErrorMsg>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <Label htmlFor="billingProvince">Province</Label>
                        <Select
                          id="billingProvince"
                          name="billingProvince"
                          value={form.billingProvince}
                          onChange={handleChange}
                          error={!!errors.billingProvince}
                          required
                          aria-invalid={!!errors.billingProvince}
                        >
                          {provinces.map((prov) => (
                            <option key={prov.code} value={prov.code}>
                              {prov.name}
                            </option>
                          ))}
                        </Select>
                      </div>
                    </InputRow>
                    <Label htmlFor="billingPostal">Postal Code</Label>
                    <Input
                      id="billingPostal"
                      name="billingPostal"
                      value={form.billingPostal}
                      onChange={handleChange}
                      error={!!errors.billingPostal}
                      required
                      aria-invalid={!!errors.billingPostal}
                      placeholder="A1A 1A1"
                    />
                    {errors.billingPostal && (
                      <ErrorMsg>{errors.billingPostal}</ErrorMsg>
                    )}
                    <Label htmlFor="billingCountry">Country</Label>
                    <Input
                      id="billingCountry"
                      name="billingCountry"
                      value="Canada"
                      disabled
                      style={{ background: "#f0f0f0" }}
                    />
                  </>
                )}
              </Section>
              <ButtonRow>
                <Button
                  type="button"
                  secondary
                  onClick={() => setShowGuestForm(false)}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={loading || Object.keys(validate()).length > 0}
                >
                  {loading ? "Placing Order..." : "Place Order"}
                </Button>
              </ButtonRow>
            </form>
          )}
          {showOrderSummary && <OrderSummary />}
          {orderError && (
            <PaymentErrorContainer data-payment-error>
              <ErrorIcon>⚠️</ErrorIcon>
              <ErrorContent>
                <ErrorTitle>Payment Failed</ErrorTitle>
                <ErrorMessage>{orderError}</ErrorMessage>
                <ErrorAction>
                  Please check your payment information and try again, or
                  contact your bank if the problem persists.
                </ErrorAction>
                <ErrorButtonContainer>
                  <ErrorButton onClick={handleRetryPayment}>
                    Try Payment Again
                  </ErrorButton>
                  <ErrorButton className="secondary" onClick={handleEditOrder}>
                    Edit Order Details
                  </ErrorButton>
                </ErrorButtonContainer>
              </ErrorContent>
            </PaymentErrorContainer>
          )}
          {showPaymentForm && (
            <Section>
              <PaymentForm
                orderData={{
                  items: items.map((item) => ({
                    productId: item.id,
                    name: item.name,
                    price: item.price, // Use original item price (without tax)
                    quantity: item.qty,
                    image: item.images?.[0],
                  })),
                  totalAmount: parseFloat(total.toFixed(2)), // Add total amount including tax
                  customerEmail: form.email,
                  customerName: form.name,
                  shippingAddress: {
                    street: `${form.address1}${
                      form.address2 ? `, ${form.address2}` : ""
                    }`,
                    city: form.city,
                    state: form.province,
                    zipCode: form.postal,
                    country: form.country,
                  },
                  billingAddress: form.useShippingAsBilling
                    ? {
                        street: `${form.address1}${
                          form.address2 ? `, ${form.address2}` : ""
                        }`,
                        city: form.city,
                        state: form.province,
                        zipCode: form.postal,
                        country: form.country,
                      }
                    : {
                        street: `${form.billingAddress1}${
                          form.billingAddress2
                            ? `, ${form.billingAddress2}`
                            : ""
                        }`,
                        city: form.billingCity,
                        state: form.billingProvince,
                        zipCode: form.billingPostal,
                        country: form.billingCountry,
                      },
                }}
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentError={handlePaymentError}
              />
            </Section>
          )}
        </LeftPanel>
        <RightPanel>
          <SectionTitle>Order Summary</SectionTitle>
          <ProductList>
            {items.map((item) => (
              <ProductItem key={item.id}>
                {item.images && item.images[0] && (
                  <ProductImage src={item.images[0]} alt={item.name} />
                )}
                <ProductName>{item.name}</ProductName>
                <ProductQty>x{item.qty}</ProductQty>
                <ProductPrice>${item.price.toFixed(2)}</ProductPrice>
                <ProductPrice>
                  ${(item.price * item.qty).toFixed(2)}
                </ProductPrice>
              </ProductItem>
            ))}
          </ProductList>
          <SummaryRow>
            <span>Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </SummaryRow>
          <SummaryRow>
            <span>Shipping</span>
            <span>${shippingCost.toFixed(2)}</span>
          </SummaryRow>
          {taxes.map((tax) => (
            <SummaryRow key={tax.label}>
              <span>{tax.label}</span>
              <span>${tax.amount.toFixed(2)}</span>
            </SummaryRow>
          ))}
          <SummaryTotal>
            <span>Total</span>
            <span>${total.toFixed(2)}</span>
          </SummaryTotal>
        </RightPanel>
      </CheckoutContainer>
    </PageWrapper>
  );
};

export default CheckoutPage;
